import { useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { NULL_FUNCTION } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { Dialog } from '../../components/Dialog';

import { DirectorsResultsList } from './DirectorsResultsList';
import { DirectorsSelectionGrid } from './DirectorsSelectionGrid';
import { useSelectAllRowSelectionModel } from './hooks/useSelectAllRowSelectionModel.hook';
import type { ArmedDirector } from './DirectorsResultsList';
import type { ActiveMembership } from './types/active-membership.type';
import type { OrganizationSearchDirectorPersonnePhysique } from './types/organization-search-result.type';

interface Props {
  readonly armedDirectors: readonly ArmedDirector[] | null;
  readonly directors: readonly OrganizationSearchDirectorPersonnePhysique[];
  readonly fallbackCivilityId: Id;
  readonly hasPendingRows: boolean;
  readonly isOpen: boolean;
  readonly onActiveMembershipsChange: (
    activeMemberships: readonly ActiveMembership[],
  ) => void;
  readonly onCancel: () => void;
  readonly onContinue: () => void;
  readonly onPendingChange: (hasPending: boolean) => void;
  readonly onStartCreation: (
    selectedDirectors: readonly OrganizationSearchDirectorPersonnePhysique[],
  ) => void;
}

export const DirectorsConfirmDialog = ({
  armedDirectors,
  directors,
  fallbackCivilityId,
  hasPendingRows,
  isOpen,
  onActiveMembershipsChange,
  onCancel,
  onContinue,
  onPendingChange,
  onStartCreation,
}: Readonly<Props>) => {
  const {
    resolveSelectedIndices,
    rowSelectionModel,
    selectedCount,
    setRowSelectionModel,
  } = useSelectAllRowSelectionModel({ items: directors });

  const handleConfirmSelection = useCallback(() => {
    const selectedIds = resolveSelectedIndices();

    onStartCreation(
      directors.filter((_director, index) => selectedIds.has(index)),
    );
  }, [directors, onStartCreation, resolveSelectedIndices]);

  const isResultsPhase = armedDirectors !== null;

  const content = isResultsPhase ? (
    <DirectorsResultsList
      armedDirectors={armedDirectors}
      fallbackCivilityId={fallbackCivilityId}
      onActiveMembershipsChange={onActiveMembershipsChange}
      onPendingChange={onPendingChange}
    />
  ) : (
    <Stack spacing={1}>
      <Typography variant="body2">
        Créer une Personne (et un membre représentant légal) pour chacun de ces
        dirigeants ? Décochez ceux que vous ne souhaitez pas créer.
      </Typography>
      <DirectorsSelectionGrid
        directors={directors}
        onRowSelectionModelChange={setRowSelectionModel}
        rowSelectionModel={rowSelectionModel}
      />
    </Stack>
  );

  return (
    <Dialog
      confirmLabel={
        isResultsPhase || selectedCount === 0
          ? 'Continuer'
          : 'Créer les Personnes sélectionnées'
      }
      content={content}
      id="organization-search-directors-confirm-dialog"
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
      isConfirmDisabled={!!(isResultsPhase && hasPendingRows)}
      isVisible={isOpen}
      onCancel={onCancel}
      onConfirm={isResultsPhase ? onContinue : handleConfirmSelection}
      setIsVisible={NULL_FUNCTION}
      showCancel={!isResultsPhase}
      title="Dirigeants détectés"
    />
  );
};
